import { Stack, Typography } from '@mui/material';
import type {
  AzureRestorePointData,
  ConvertOtherSnapshotsOnDemandRequest,
  ConvertOtherSnapshotsOnDemandRequestSnapshotDetailsInner,
  RecoveryPointData,
  InventoryResource,
  Provider,
} from '@repo/api-gw-sdk';
import { useEffect, useState, type Dispatch, type SetStateAction } from 'react';

import { useDAL } from '@/contexts/useDAL';
import { useWorkspace } from '@/contexts/useWorkspace';
import { CloudProviders } from '@/data/inventory/data';
import type { BackupVault } from '@/data/vaults/backupVault';
import { extractDiskName } from '@/utils/azure';
import { dayjs, DATE_FORMAT } from '@/utils/dayjs';

import { PanelWrapper } from './panelWrapper';

import { SnapshotIndicator } from '../snapshots/SnapshotIndicator';
import type { CalcOption } from '../snapshots/convertToEon/calcRetentionFromSelector';
import { ConvertLifetimeLayout } from '../snapshots/convertToEon/convertLifetimeLayout';
import { ConvertToEonProviderDetails } from '../snapshots/convertToEon/convertToEonProviderDetails';
import { ConvertToEonTargetDetails } from '../snapshots/convertToEon/convertToEonTargetDetails';
import { OtherSnapshotDot } from '../snapshots/otherSnapshotDot';

interface ConvertToSingleEonSnapshotPanelProps {
  resource: InventoryResource;
  onClose: () => void;
  onBack: () => void;
  snapshotDetails?: ConvertOtherSnapshotsOnDemandRequestSnapshotDetailsInner[];
  snapshotDate: string | Date;
  azureRestorePoint?: AzureRestorePointData;
  recoveryPoint?: RecoveryPointData;
}

export function ConvertToSingleEonSnapshotPanel(
  props: ConvertToSingleEonSnapshotPanelProps
) {
  const dal = useDAL();
  const { snackbar } = useWorkspace();
  const [isInvalidRetention, setIsInvalidRetention] = useState(false);
  const {
    resource,
    snapshotDetails,
    snapshotDate,
    azureRestorePoint,
    recoveryPoint,
    onClose,
    onBack,
  } = props;
  const [convertDetails, setConvertDetails] =
    useState<ConvertOtherSnapshotsOnDemandRequest>({
      vaultId: '',
      retentionDays: 0,
      snapshotDetails: snapshotDetails || [],
    });

  return (
    <PanelWrapper
      header={{
        onBack,
        title: 'Convert to Eon snapshot',
        onClose,
      }}
      footer={{
        primaryButton: {
          'data-testid': 'convert-to-single-eon-button',
          icon: 'material-symbols-join-right-rounded',
          text: 'Convert',
          onClick: async () => {
            await dal.convert.otherSnapshotOnDemand(
              resource.id,
              convertDetails
            );
            onClose();
            snackbar.showMessage(
              `Backup jobs started for ${resource.resourceName}`
            );
          },
          disabled: isInvalidRetention,
        },
        secondaryButton: {
          'data-testid': 'convert-to-eon-cancel-button',
          text: 'Cancel',
          onClick: onBack,
        },
      }}
      testIdPrefix='convert-to-eon-snapshots-panel'
    >
      <ConvertToEonSingleBody
        setConvertDetails={setConvertDetails}
        snapshotDetails={snapshotDetails}
        snapshotDate={snapshotDate}
        azureRestorePoint={azureRestorePoint}
        recoveryPoint={recoveryPoint}
        setIsInvalidRetention={setIsInvalidRetention}
        isInvalidRetention={isInvalidRetention}
        cloudProvider={resource.cloudProvider}
      />
    </PanelWrapper>
  );
}

interface ConvertToEonSingleBodyProps {
  setConvertDetails: Dispatch<
    SetStateAction<ConvertOtherSnapshotsOnDemandRequest>
  >;
  snapshotDetails?: ConvertOtherSnapshotsOnDemandRequestSnapshotDetailsInner[];
  snapshotDate: string | Date;
  setIsInvalidRetention: (value: boolean) => void;
  isInvalidRetention: boolean;
  cloudProvider: Provider;
  azureRestorePoint?: AzureRestorePointData;
  recoveryPoint?: RecoveryPointData;
}

const ConvertToEonSingleBody = ({
  setConvertDetails,
  snapshotDetails,
  snapshotDate,
  azureRestorePoint,
  recoveryPoint,
  setIsInvalidRetention,
  isInvalidRetention,
  cloudProvider,
}: ConvertToEonSingleBodyProps) => {
  const [calcOption, setCalcOption] = useState<CalcOption>('now');
  const [selectedVault, setSelectedVault] = useState<BackupVault>();
  const [retentionInDays, setRetentionInDays] = useState(90);

  const getRetentionForConvertCall = () => {
    if (calcOption === 'now') {
      return retentionInDays;
    }
    const expirationDate = dayjs
      .utc(snapshotDate)
      .add(retentionInDays, 'day')
      .startOf('day');
    const today = dayjs.utc().startOf('day');
    return expirationDate.diff(today, 'day');
  };

  useEffect(() => {
    if (selectedVault) {
      setConvertDetails((prev) => ({
        ...prev,
        vaultId: selectedVault.id,
        ...(azureRestorePoint && {
          azureRestorePoints: [
            {
              restorePointId: azureRestorePoint.restorePointId,
              region: azureRestorePoint.location,
              diskIds: azureRestorePoint.diskIds,
              vaultName: azureRestorePoint.vaultName,
              vaultResourceGroup: azureRestorePoint.vaultResourceGroup,
              vaultType: azureRestorePoint.vaultType,
              recoveryPointTime:
                typeof azureRestorePoint.createdAt === 'string'
                  ? dayjs.utc(azureRestorePoint.createdAt).toDate()
                  : azureRestorePoint.createdAt,
            },
          ],
        }),
        ...(recoveryPoint && {
          recoveryPoints: [
            {
              backupId: recoveryPoint.backupId,
              region: recoveryPoint.region,
            },
          ],
        }),
      }));
    }
  }, [selectedVault, setConvertDetails, azureRestorePoint, recoveryPoint]);

  useEffect(() => {
    setConvertDetails((prev) => ({
      ...prev,
      retentionDays: getRetentionForConvertCall(),
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [retentionInDays, setRetentionInDays]);

  useEffect(() => {
    setConvertDetails((prev) => ({
      ...prev,
      retentionDays: getRetentionForConvertCall(),
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [calcOption]);

  const fields = azureRestorePoint
    ? [
        {
          label: 'Snapshot date',
          value: dayjs.utc(azureRestorePoint.createdAt).format(DATE_FORMAT),
        },
        {
          label: 'Disks',
          value: (
            <Stack direction='column' gap='8px'>
              {azureRestorePoint.diskIds?.map((id) => (
                <Typography
                  key={id}
                  sx={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    maxWidth: '400px',
                  }}
                >
                  {extractDiskName(id)}
                </Typography>
              ))}
            </Stack>
          ),
        },
      ]
    : recoveryPoint
      ? [
          {
            label: 'Backup date',
            value: dayjs.utc(recoveryPoint.creationTime).format(DATE_FORMAT),
          },
          {
            label: 'Backup ID',
            value: <Typography>{recoveryPoint.backupId}</Typography>,
          },
        ]
      : [
          {
            label: 'Snapshot date',
            value: dayjs.utc(snapshotDate).format(DATE_FORMAT),
          },
          {
            label: 'Volume snapshot IDs',
            value: (
              <Stack direction='column' gap='16px'>
                {snapshotDetails?.map((snap) => (
                  <Typography key={snap.snapshotLocation.snapshotId}>
                    {snap.snapshotLocation.snapshotId}
                  </Typography>
                ))}
              </Stack>
            ),
          },
        ];

  return (
    <ConvertLifetimeLayout
      description={
        <Typography
          width='90%'
          paddingX='40px'
          lineHeight={'20px'}
          fontWeight={300}
          variant='subtitle1'
          fontSize={14}
        >
          The {CloudProviders[cloudProvider]?.displayName} snapshot will be
          copied and converted to Eon’s snapshot format, allowing you to search
          and explore your data. You can set the retention based on the current
          date or the original snapshot date.
          <br /> <br />
          The original snapshot won’t be deleted.
        </Typography>
      }
      source={{
        dot: <OtherSnapshotDot />,
        title: `${CloudProviders[cloudProvider]?.displayName} snapshot`,
        details: <ConvertToEonProviderDetails fields={fields} />,
      }}
      target={{
        dot: (
          <SnapshotIndicator
            sx={{
              margin: '6px',
            }}
            backgroundColor={selectedVault?.backgroundColor || ''}
          />
        ),
        title: 'Eon snapshot',
        details: (
          <ConvertToEonTargetDetails
            setSelectedVault={setSelectedVault}
            selectedVault={selectedVault}
            setRetentionInDays={setRetentionInDays}
            retentionInDays={retentionInDays}
            calcOption={calcOption}
            setCalcOption={setCalcOption}
            snapshotDate={dayjs.utc(snapshotDate).toDate()}
            setIsInvalidRetention={setIsInvalidRetention}
            isInvalidRetention={isInvalidRetention}
          />
        ),
      }}
    />
  );
};
